import React, { useEffect, useState } from "react";
import { resetCurrentWorkspace } from "app-redux/actions/licensingAccounts.actions";
import { AppState } from "app-redux/reducers";
import { getCurrentUser } from "app-redux/selectors/accounts.selector";
import { getCurrentWorkspace, getDefaultWorkspace } from "app-redux/selectors/licensingAccounts.selector";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import "./styles.less";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { history, RoutePaths } from "core/history";
import { Button, Col, Form, Input, Popconfirm, Row, Select, Tooltip, Upload, message } from "antd";
import {
  InfoCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import CountryDropdown from "components/CountryDropdown";
import { AppConstants } from "core/app.constants";
import {
  ButtonType,
  notification,
} from "syngenta-digital-cropwise-react-ui-kit";
import CustomModal from "components/Modal";
import { isUK } from "utils/common-methods";
import { IdentityProvider } from "utils/enums";
import { IPictureDTO, IUserAccount, PasswordRecoveryDTO } from "core/accounts/models";
import i18next from "i18next";
import { INavigationRouteProps } from "core/navigation/models";
import { UUID } from "core/utils/BasicModels";
import { retrieveAccessToken } from "app-redux/actions/login.actions";
import {
  accountRecoveryEmailSender,
  deleteAccount,
  deleteAccountPhoto,
  editUserProfile,
  uploadAccountPhoto,
} from "app-redux/actions/profile.actions";
import { PhoneInputBox } from "components/phone-input-box";
import arrowBack from 'assets/icons/arrowBack.svg';
import deleteIcon from 'assets/icons/deleteIcon.svg';
import warning from 'assets/icons/warning.svg';
import infoIcon from 'assets/icons/infoIcon.svg';
import removePhotoIcon from 'assets/icons/removePhotoIcon.svg';
import { fetchUnsignedLegalDocuments } from "app-redux/actions/legal_documents.action";
import { translationWrapper } from '../../utils/common-methods';
import { triggerUserDeletionFlow } from "services/MemberService";
import { SegmentService } from 'services/SegmentService';
import ProfilePhotoViewer from "./ProfilePhotoViewer";
import ProfilePhotoInfoPopover from "./ProfilePhotoInfoPopover";

interface IProfile extends INavigationRouteProps<{}> {
  currentUser?: IUserAccount | any;
  deleteAccountRequest: (accountId: UUID) => Promise<string>;
  recoveryUserPassword: (recovery: PasswordRecoveryDTO) => Promise<string>;
  resetWorkspace: () => any;
  editProfile: (values: any, currentUser: IUserAccount) => Promise<string>;
  checkPassword: (username: string, password: string) => Promise<string>;
  uploadAccountPhoto: (accountId: UUID, photo?: string | Blob) => Promise<IPictureDTO>;
  deleteAccountPhoto: (accountId: UUID) => Promise<void>;
  currentWorkspaceId: string;
  isSignLegelDocumentsEnabled: boolean;
}

const WarningContent: React.FC = () => {
  const { t } = translationWrapper(useTranslation());
  return (
    <>
      <p>
        {t("description.if_you_confirm_you_will_delete_your_account")}{" "}
        <b>
          {isUK() ? "Syngenta Digital" : t("heading.cropwise")} {t("heading.apps")}
        </b>
        . {t("description.it_cannot_be_undone")}
      </p>
      <p style={{ color: "#4D5165" }}>
        {t(
          "description.your_account_will_be_deleted_immediately"
        )}
      </p>
    </>
  );
};

const DeleteAccountWarningModal: React.FC<DeleteAccountWarningModalProps> = ({setIsWarningModalVisible,setIsPasswordModalVisible,isWarningModalVisible}) => {
  const { t } = translationWrapper(useTranslation());
  return (
    <CustomModal
      className="edit-profile-delete-modal"
      cancelButton={{
        text: t("global.button.cancel"),
        onCancel: () => {
          setIsWarningModalVisible(false);
        },
      }}
      content={<WarningContent />}
      icon={
        <span><img src={warning} alt="warning"/></span>
      }
      okButton={{
        text: t("global.button.continue"),
        onContinue: () => {
          setIsWarningModalVisible(false);
          setIsPasswordModalVisible(true);
        },
      }}
      title={t("title.do_you_want_to_delete_your_account")}
      visible={isWarningModalVisible}
    />
  );
};

interface DeleteAccountWarningModalProps {
  setIsWarningModalVisible?: any;
  setIsPasswordModalVisible?: any;
  isWarningModalVisible?: any;
}

const CheckPasswordForm: React.FC<{
  checkForm: any;
  setIsPasswordButtonDisabled: any;
}> = ({ checkForm, setIsPasswordButtonDisabled }) => {
  const { t } = translationWrapper(useTranslation());
  return (
    <Form
      form={checkForm}
      layout="vertical"
      name="form_in_modal"
      initialValues={{ modifier: "public" }}
    >
      <Form.Item
        name="password"
        label={<label style={{ color: "#14151c" }}>{t("title.password")}</label>}
        rules={[
          {
            required: true
          },
        ]}
      >
        <Input.Password
          placeholder={t("place_holder.enter_password")}
          onChange={() => {
            checkForm
              .validateFields()
              .then(() => {
                setIsPasswordButtonDisabled(false);
              })
              .catch(() => {
                setIsPasswordButtonDisabled(true);
              });
          }}
        />
      </Form.Item>
    </Form>
  );
};

const CheckPasswordModal: React.FC<CheckPasswordModalProps> = ({
  setIsPasswordModalVisible,
  isPasswordModalButtonLoading,
  isPasswordButtonDisabled,
  isPasswordModalVisible,
  setIsPasswordButtonDisabled,
  onDelete
}) => {
  const { t } = translationWrapper(useTranslation());
  const [passwordForm] = Form.useForm();
  return (
    <CustomModal
      className="edit-profile-password-modal"
      cancelButton={{
        text: t("global.button.cancel"),
        onCancel: () => {
          setIsPasswordModalVisible(false);
          passwordForm.resetFields();
        },
      }}
      content={CheckPasswordForm({ checkForm: passwordForm, setIsPasswordButtonDisabled })}
      height="221px"
      icon={
        <span><img src={warning} alt="warning"/></span>
      }
      okButton={{
        text: t("global.button.delete"),
        onContinue: async () => {
          const data = await passwordForm.validateFields();
          await onDelete(data.password);
        },
        loading: isPasswordModalButtonLoading,
        disabled: isPasswordButtonDisabled,
      }}
      title={t("heading.enter_your_password_to_delete_your_account")}
      visible={isPasswordModalVisible}
      width="423px"
    />
  );
};

interface CheckPasswordModalProps {
  setIsPasswordModalVisible?: any;
  isPasswordModalButtonLoading?: any;
  isPasswordButtonDisabled?: any;
  isPasswordModalVisible?: any;
  setIsPasswordButtonDisabled?: any;
  onDelete?: any;
}

const IncorrectPasswordModal: React.FC<IncorrectPasswordModalProps> = ({
  setIsWrongPasswordModalVisible,
  setIsPasswordModalVisible,
  isWrongPasswordModalVisible
}) => {
  const { t } = translationWrapper(useTranslation());
  return (
    <CustomModal
      className="edit-profile-incorrect-password-modal"
      content={<p>{t("heading.please_try_again")}</p>}
      height="168px"
      width="423px"
      icon={
        <span><img src={warning} alt="warning"/></span>
      }
      okButton={{
        text: t("global.button.ok"),
        onContinue: () => {
          setIsWrongPasswordModalVisible(false);
          setIsPasswordModalVisible(true);
        },
      }}
      title={t("heading.incorrect_password")}
      visible={isWrongPasswordModalVisible}
    />
  );
};

interface IncorrectPasswordModalProps {
  setIsWrongPasswordModalVisible?: any;
  setIsPasswordModalVisible?: any;
  isWrongPasswordModalVisible?: any;
}

const ChangePasswordLink: React.FC<{currentUser: any; onRecoveryPassword: any}> = ({currentUser, onRecoveryPassword}) => {
  const { t } = translationWrapper(useTranslation());
  if (
    currentUser &&
    currentUser.identity_provider === IdentityProvider.LOCAL
  ) {
    return (
      <a>
        <span
          className="edit-profile-change-password"
          onClick={onRecoveryPassword}
        >
          {t("button.change_my_password")}
        </span>
      </a>
    );
  }
  return null;
};

const RecoverPasswordModal: React.FC<{setIsRecoveryPasswordModalVisible: any; isRecoveryPasswordModalVisible: any}> = ({setIsRecoveryPasswordModalVisible, isRecoveryPasswordModalVisible}) => {
  const { t } = translationWrapper(useTranslation());
  return (
    <CustomModal
      className="recover-password-modal"
      okButton={{
        text: t("global.button.ok"),
        onContinue: () => {
          setIsRecoveryPasswordModalVisible(false);
        },
      }}
      title={t("notification.check_mail")}
      visible={isRecoveryPasswordModalVisible}
      width="360px"
      content={t(
        "notification.we_have_sent_a_password_recover"
      )}
      height={"170px"}
    />
  );
};

const RecoverPasswordFailedModal: React.FC<{setIsRecoveryPasswordFailedModalVisible: any, isRecoveryPasswordFailedModalVisible: any}> = ({setIsRecoveryPasswordFailedModalVisible, isRecoveryPasswordFailedModalVisible}) => {
  const { t } = translationWrapper(useTranslation());
  return (
    <CustomModal
      className="recover-password-failed-modal recover-password-modal"
      okButton={{
        text: t("global.button.ok"),
        onContinue: () => {
          setIsRecoveryPasswordFailedModalVisible(false);
        },
      }}
      title={t("notification.request_failed")}
      visible={isRecoveryPasswordFailedModalVisible}
      width="360px"
      content={t("notification.the_password_change_request_failed")}
      height={"170px"}
    />
  );
};

const DeleteAccountBtn: React.FC<{currentUser:any, setIsWarningModalVisible: any}> = ({currentUser, setIsWarningModalVisible}) => {
  const { t } = translationWrapper(useTranslation());
  return (
    <div>
    <Button
      disabled={
        currentUser &&
        currentUser.identity_provider !== IdentityProvider.LOCAL
      }
      className="accounts__drawer__buttons__deleteButton"
    >
      <img src={deleteIcon} className="accounts__drawer__buttons__deleteButton__icon" />
      <span
        className="accounts__drawer__buttons__deleteButton--action"
        onClick={() => {
          setIsWarningModalVisible(true);
        }}
      >
        {t("button.delete_account")}
      </span>
    </Button>
    </div>
  );
};

const triggerDeletionFlow = async (user:any) => {
  const TRIGGER_DELETION_FLOW = AppConstants.TRIGGER_DELETION_FLOW;
  const payload = {
      "fields": {
        "project":TRIGGER_DELETION_FLOW.project,
        "summary": TRIGGER_DELETION_FLOW.summary,
        "description": `User: ${user.id} \n\n Mail User: ${user.email}`,
        "issuetype": TRIGGER_DELETION_FLOW.issuetype,
        "labels": TRIGGER_DELETION_FLOW.labels
    }
  };
  return await triggerUserDeletionFlow(payload);
}

export const ProfileContainer: React.FC<IProfile> = ({
  currentUser,
  editProfile,
  recoveryUserPassword,
  checkPassword,
  resetWorkspace,
  deleteAccountRequest,
  uploadAccountPhoto,
  deleteAccountPhoto,
  currentWorkspaceId,
  isSignLegelDocumentsEnabled
}) => {
  const jobTitle = get(currentUser, 'job_title', null);
  const photoUri = get(currentUser, 'photo_uri', undefined);
  const { t } = translationWrapper(useTranslation());
  const [form] = Form.useForm();
  const [isEditButtonDisabled, setIsEditButtonDisabled] = useState(false);
  const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isPasswordButtonDisabled, setIsPasswordButtonDisabled] =
    useState(true);
  const [isPasswordModalButtonLoading, setIsPasswordModalButtonLoading] =
    useState(false);
  const [isWrongPasswordModalVisible, setIsWrongPasswordModalVisible] =
    useState(false);
  const [isRecoveryPasswordModalVisible, setIsRecoveryPasswordModalVisible] =
    useState(false);
  const [
    isRecoveryPasswordFailedModalVisible,
    setIsRecoveryPasswordFailedModalVisible,
  ] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [photoSrc, setPhotoSrc] = useState<string>();
  const [binaryPhotoSrc, setBinaryPhotoSrc] = useState(photoUri);

  useEffect(() => {
    if (jobTitle) {
      SegmentService().identify({ jobTitle });
    }
  }, [jobTitle]);

  useEffect(() => {
    if (photoUri) {
      setPhotoSrc(photoUri);
    }
  }, [photoUri]);

  function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  }

  const onDelete = async (password: string) => {
    setIsPasswordModalButtonLoading(true);
    try {
      await checkPassword(currentUser?.email, password);
    } catch (e) {
      setIsPasswordModalButtonLoading(false);
      setIsPasswordModalVisible(false);
      setIsWrongPasswordModalVisible(true);
      return;
    }
    setIsPasswordModalVisible(false);
    try {
      await triggerDeletionFlow(currentUser);
      await deleteAccountRequest(currentUser?.id);
    } catch (e) {
      onCloseAndReset();
      errorHandler(
        t("notification.failed_to_delete_account"),
        t("notification.the_delete_request_failed")
      );
      return;
    }
    onCloseAndReset();
    successHandler(t('notification.your_account_has_been_deleted'),t('notification.your_data_will_be_deleted_within_days_when_done'))
    setTimeout(() => {
      window.location.href = `${process.env.OAUTH_URL}/signin?account_deleted=true`;
    },2500);
  };

  const onHandleInputChange = () => {
    const isValidName = (name: string) => {
      return name.length > 0;
    };

    const isValidPhone = (phone: string | null) => {
      if (phone === null) {
        return true;
      } else {
        return phone.length <= 20;
      }
    };
    // tslint:disable
    form
      .validateFields(["name", "phone"])
      .then((editedUser) => {
        if (isValidName(editedUser.name) && isValidPhone(editedUser.phone)) {
          setIsEditButtonDisabled(false);
        } else {
          setIsEditButtonDisabled(true);
        }
      })
      .catch(() => {
        setIsEditButtonDisabled(true);
      });
    // tslint:enable
  };

  const [windowSize, setWindowSize] = useState(getWindowDimensions().width);

  const errorHandler = (message: string, description: string) => {
    notification.error({
      message,
      description,
      placement: "topRight",
    });
  };

  const successHandler = (message: string, description: string) => {
    notification.success({
      message,
      description,
      placement: "topRight",
      className: 'edit-profile-success-notification'
    });
  };

  const onRecoveryPassword = async () => {
    const passwordRecovery = {
      locale: currentUser?.locale || "en",
      email: currentUser?.email,
    } as PasswordRecoveryDTO;
    // tslint:disable
    recoveryUserPassword(passwordRecovery)
      .then(() => {
        if (windowSize < 719) {
          setIsRecoveryPasswordModalVisible(true);
          setIsRecoveryPasswordFailedModalVisible(false);
          onCloseAndReset();
        } else {
          successHandler(
            t("notification.check_mail"),
            t("notification.we_have_sent_a_password_recover")
          );
        }
      })
      .catch(() => {
        onCloseAndReset();
        if (windowSize < 719) {
          setIsRecoveryPasswordModalVisible(false);
          setIsRecoveryPasswordFailedModalVisible(true);
        } else {
          errorHandler(
            t("notification.request_failed"),
            t("notification.the_password_change_request_failed")
          );
        }
      });
    // tslint:enable
  };

  const onCloseAndReset = () => {
    form.resetFields();
    setIsLoading(false);
    setIsEditButtonDisabled(false);
  };

  const checkIfUserHasDocumentsToSign = (countryCode: string) => {
    fetchUnsignedLegalDocuments(countryCode).then((response) => {
      if (response.length > 0 && isSignLegelDocumentsEnabled) {
        window.location.href = `${process.env.OAUTH_URL}/sign-legal-documents`;
      } else {
        history.push({
          pathname: RoutePaths.OVERVIEW(),
          search: location.search,
        });
      }
    })
  }

  const onEdit = async (values: any) => {
    localStorage.setItem("currentLanguage", values.locale);
    i18next.changeLanguage(localStorage.getItem("currentLanguage") || "en");
    if (binaryPhotoSrc) {
      await uploadAccountPhoto(currentUser.id, binaryPhotoSrc)
        .catch(() => {
          errorHandler(
            t("notification.failed_to_update_picture"),
            t("notification.the_edit_request_failed")
          )
        });
    }
    // tslint:disable
    editProfile(values, currentUser)
      .then(() => {
        onCloseAndReset();
        successHandler(
          t("notification.successfully_edited"),
          t("notification.profile_edited_with_success")
        );
        if (values?.country_code !== currentUser?.country_code) {
          checkIfUserHasDocumentsToSign(values?.country_code);
        }
      })
      .catch(() => {
        onCloseAndReset();
        errorHandler(
          t("notification.failed_to_edit_account"),
          t("notification.the_edit_request_failed")
        );
      });
    // tslint:enable
  };

  const onEditUser = async () => {
    setIsLoading(true);
    const values = await form.validateFields();
    await onEdit(values);
  };

  const redirectToAccountsPage = async () => {
    history.replace(RoutePaths.WORKSPACES(currentWorkspaceId));
    await resetWorkspace().catch();
  };

  const beforeUpload = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error(t('photo.image_type_error_message'));
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t('photo.image_size_error_message'));
    }
    return isJpgOrPng && isLt2M;
  };

  const getBase64 = async (file: Blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const getUrl = (icon: string) => {
    if (!icon.includes('data:image')) {
      return `${icon}?${new Date().getTime()}`;
    } else {
      return icon;
    }
  };

  const handleAccountPhotoChange = async (info: any) => {
    if (info.file.originFileObj) {
      if (!info.file.url) {
        info.file.preview = await getBase64(info.file.originFileObj);
      }
      setPhotoSrc(getUrl(info.file.preview));
      setBinaryPhotoSrc(info.file.originFileObj);
    }
  };

  const handleDeleteAccountPhoto = async () => {
    await deleteAccountPhoto(currentUser.id)
      .then(() => {
        successHandler(
          t('photo.picture_deleted_successfully'),
          ""
        )
      })
      .catch(() => {
        errorHandler(
          t("notification.failed_to_delete_picture"),
          t("notification.the_delete_request_failed")
        )
      });
    setPhotoSrc(undefined);
  }

  useEffect(() => {
    form.setFieldsValue({
      name: currentUser?.name,
      first_name: currentUser?.first_name,
      last_name: currentUser?.last_name,
      email: currentUser?.email,
      phone: currentUser?.phone,
      clock_format: currentUser?.clock_format,
      date_format: currentUser?.date_format,
      locale: currentUser?.locale,
      country_code: currentUser?.country_code,
      role: currentUser?.role,
      job_title: currentUser?.job_title
    });
  }, [currentUser]);

  useEffect(() => {
    function handleResize() {
      setWindowSize(getWindowDimensions().width);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <div className="edit-profile-page-main-container">
      <div className="edit-profile-title-box">
        <div className="edit-profile-title-icon" onClick={() => redirectToAccountsPage()}><img src={arrowBack} alt={t("Go Back")}/></div>
        <div className="edit-profile-title">{t('global.title.edit_profile')}</div>
      </div>
      <div className="edit-profile-page-container">
        <Form
          form={form}
          layout="vertical"
          name="form_in_drawer"
          initialValues={currentUser}
          onChange={onHandleInputChange}
        >
          <span className="accounts__drawer__section"> {t("title.about_you")}</span>
          <div className="accounts__drawer__about">
            <div className="accounts__drawer__about__basic__info">
              <div className="profile__picture__section">
                <Form.Item
                  name="photo"
                  label={(
                    <div style={{ display: 'flex', flexDirection: 'row'}}>
                      {t("title.profile_picture")}
                      <ProfilePhotoInfoPopover t={t}>
                        <div className="info__icon__container" >
                          <img src={infoIcon} alt="infoIcon"/>
                        </div>
                      </ProfilePhotoInfoPopover>
                    </div>
                  )}
                >
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Upload
                      name="icon"
                      listType="picture-card"
                      className="photo__uploader"
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      onChange={handleAccountPhotoChange}
                    >
                      <ProfilePhotoViewer
                        isLoading={isLoading}
                        photoSrc={photoSrc}
                        t={t}
                        onPhotoError={() => setPhotoSrc(undefined)}
                      />
                    </Upload>
                    {
                      photoSrc && (
                        <Popconfirm
                          title={t('photo.popconfirm_delete_picture')}
                          placement="bottom"
                          icon={<ExclamationCircleOutlined />}
                          onConfirm={handleDeleteAccountPhoto}
                        >
                          <img className="remove__photo__icon" src={removePhotoIcon} alt="Remove Photo Icon" />
                        </Popconfirm>
                      )
                    }
                  </div>
                </Form.Item>
              </div>
              <div className="basic__info__section">
                <Form.Item
                  name="first_name"
                  label={t("title.first_name")}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="last_name" label={t("title.last_name")}>
                  <Input />
                </Form.Item>
                <Row justify="space-between">
                  <Col>
                    <span>{t("global.label.mail")}</span>
                  </Col>
                  <Col>
                    <Tooltip title={t("description.we_use_your_email_to_set_up_and_authenticate_your_account")}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Col>
                </Row>
                <Form.Item name="email">
                  <Input disabled={true} />
                </Form.Item>
              </div>
            </div>
            <Row justify="space-between">
              <Col>
                <span>{t("title.location_of_residence")}</span>
              </Col>
              <Col>
                <Tooltip title={t("description.we_use_your_location_to_show_the_correct_terms_and_notices")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Col>
            </Row>
            <Form.Item
              name="country_code"
              id={"country_code_dropdown"}
              className="accounts__drawer__preferences__lastSelect selected-country-dropdown"
            >
              <CountryDropdown value={currentUser?.country_code}/>
            </Form.Item>

            <Row justify="space-between">
              <Col>
                <span>{t("title.mobile_number")}</span>
              </Col>
              <Col>
                <Tooltip title={t("description.we_use_your_phone_number_to_contact_you_for_customer_support")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Col>
            </Row>
            <Form.Item name="phone">
            <PhoneInputBox
                error={false}
                value={currentUser?.phone} onChange={undefined} autoFocus={false} />
            </Form.Item>

            <div style={{marginTop:"16px"}}>
              <Row justify="space-between">
                <Col>
                  <span>{t("title.job_title")}</span>
                </Col>
                <Col>
                  <Tooltip title={t("description.we_use_your_job_title_to_customize_suggestions_and_support")}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Col>
              </Row>

              <Form.Item
                name="job_title"
                className="job-title-box"
              >
                <Select
                  placeholder={t("place_holder.select_a_job_title")}
                  dropdownClassName="edit-profile-dropdown-container"
                  value={(currentUser && currentUser.job_title) ? currentUser.job_title : null}
                  options={[
                    {
                      value: 'GROWER',
                      label: t('global.job_titles.GROWER'),
                    },
                    {
                      value: 'MANAGER',
                      label: t('global.job_titles.MANAGER'),
                    },
                    {
                      value: 'SYNGENTA_INTERNAL',
                      label: t('global.job_titles.SYNGENTA_INTERNAL'),
                    },
                    {
                      value: 'DEALER',
                      label: t('global.job_titles.DEALER'),
                    },
                    {
                      value: 'CONSULTANT',
                      label: t('global.job_titles.CONSULTANT'),
                    },
                    {
                      value: 'AGRONOMIST',
                      label: t('global.job_titles.AGRONOMIST'),
                    },
                    {
                      value: 'SALES_REPRESENTATIVE',
                      label: t('global.job_titles.SALES_REPRESENTATIVE'),
                    },
                    {
                      value: 'FIELD_TECHNICIAN',
                      label: t('global.job_titles.FIELD_TECHNICIAN'),
                    },
                    {
                      value: 'OTHER',
                      label: t('global.job_titles.OTHER'),
                    },
                  ]}
                >
                </Select>
              </Form.Item>
            </div>
          </div>

          <div className="divider"></div>
          <span className="accounts__drawer__section">{t("title.preferences")}</span>

          <div className="accounts__drawer__preferences">
            <Form.Item
              name="clock_format"
              label={t("title.clock_format")}
              className="accounts__drawer__preferences__select accounts__drawer__preferences__select__box"
            >
              <Select dropdownClassName="edit-profile-dropdown-container">
                <Select.Option value="ALWAYS_12">{t("place_holder.am_pm")}</Select.Option>
                <Select.Option value="LET_LOCALE_DECIDE">
                  {t("place_holder.let_locale_decide")}
                </Select.Option>
                <Select.Option value="ALWAYS_24">{t("place_holder.24_hour")}</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="date_format"
              label={t("title.date_format")}
              className="accounts__drawer__preferences__select accounts__drawer__preferences__select__box"
            >
              <Select dropdownClassName="edit-profile-dropdown-container">
                <Select.Option value="DD-MM-YYYY">
                  {t("place_holder.dd_mm_yyyy")}
                </Select.Option>
                <Select.Option value="MM-DD-YYYY">
                  {t("place_holder.mm_dd_yyyy")}
                </Select.Option>
                <Select.Option value="YYYY-MM-DD">
                  {t("place_holder.yyyy-mm-dd")}
                </Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="locale"
            label={t("title.language")}
            style={{ marginBottom: "18px" }}
            className="accounts__drawer__preferences__lastSelect"
          >
            <Select dropdownClassName="edit-profile-dropdown-container">
              {AppConstants?.SUPPORTED_LANGS?.map((lang: any) => {
                return (
                  <Select.Option key={lang.value} value={lang.value}>
                    {t(`global.supported_language.${lang.name}`)} 
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <DeleteAccountWarningModal
            setIsWarningModalVisible={setIsWarningModalVisible}
            setIsPasswordModalVisible={setIsPasswordModalVisible}
            isWarningModalVisible={isWarningModalVisible}
          />
          <CheckPasswordModal
            setIsPasswordModalVisible={setIsPasswordModalVisible}
            isPasswordModalButtonLoading={isPasswordModalButtonLoading}
            isPasswordButtonDisabled={isPasswordButtonDisabled}
            isPasswordModalVisible={isPasswordModalVisible}
            setIsPasswordButtonDisabled={setIsPasswordButtonDisabled}
            onDelete={onDelete}
          />
          <IncorrectPasswordModal
            setIsWrongPasswordModalVisible={setIsWrongPasswordModalVisible}
            setIsPasswordModalVisible={setIsPasswordModalVisible}
            isWrongPasswordModalVisible={isWrongPasswordModalVisible}
          />
          <ChangePasswordLink
            currentUser={currentUser}
            onRecoveryPassword={onRecoveryPassword}
          />
          <RecoverPasswordModal
            setIsRecoveryPasswordModalVisible={
              setIsRecoveryPasswordModalVisible
            }
            isRecoveryPasswordModalVisible={isRecoveryPasswordModalVisible}
          />
          <RecoverPasswordFailedModal
            setIsRecoveryPasswordFailedModalVisible={
              setIsRecoveryPasswordFailedModalVisible
            }
            isRecoveryPasswordFailedModalVisible={
              isRecoveryPasswordFailedModalVisible
            }
          />
          <div className="accounts__drawer__buttons">
            <DeleteAccountBtn currentUser={currentUser} setIsWarningModalVisible={setIsWarningModalVisible} />
            <div>
            <Button
              disabled={isEditButtonDisabled}
              loading={isLoading}
              type={ButtonType.primary}
              className="accounts__drawer__buttons__lastButton accounts__drawer__buttons__saveButton"
              onClick={onEditUser}
            >
              {t("global.button.save")}
            </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState, ownProps: any) => ({
  currentUser: getCurrentUser(state),
  accountId: get(ownProps, "match.params.accountId", null),
  currentWorkspace: getCurrentWorkspace(state),
  currentWorkspaceId: getDefaultWorkspace(state),
  isProtectorBannerVisible:
    !state.rollout.loading && state.rollout?.flags?.isProtectorBannerVisible,
  isSignLegelDocumentsEnabled: get(state, 'rollout.flags.isSignLegelDocumentsEnabled', false),
});

const mapDispatchToProps = (dispatch: any) => ({
  checkPassword: (username: string, password: string) =>
    dispatch(retrieveAccessToken(username, password)),
  recoveryUserPassword: (recovery: PasswordRecoveryDTO) =>
    dispatch(accountRecoveryEmailSender(recovery)),
  editProfile: (values: any, currentUser: IUserAccount) =>
    dispatch(editUserProfile(values, currentUser)),
  resetWorkspace: () => dispatch(resetCurrentWorkspace()),
  deleteAccountRequest: (accountId: UUID) => dispatch(deleteAccount(accountId)),
  uploadAccountPhoto: (accountId: UUID, photo?: string | Blob) =>
    dispatch(uploadAccountPhoto(accountId, photo)),
  deleteAccountPhoto: (accountId: UUID) =>
    dispatch(deleteAccountPhoto(accountId)),
});

export const ProfilePage = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProfileContainer)
);
